<script setup>
// Tela de início de Vender (caminho B aprovado em 12/09/2026): cara de painel
// financeiro. À esquerda os indicadores em pilha (em aberto em destaque,
// ganhos no mês e taxa de ganho com variação), à direita o gráfico dos
// últimos 30 dias (em aberto e ganhos acumulados). Embaixo, o dia (tarefas,
// agenda, funil) e o quadro do funil visto de cima, com cada card abrindo o
// Kanban no lugar certo.
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { emitter } from 'shared/helpers/mitt';
import { useSalesHome, EMPTY } from './useSalesHome';
import './hubDesign.css';

const props = defineProps({
  world: { type: Object, required: true },
});
const { t } = useI18n();
const router = useRouter();
const { isFeatureFlagEnabled } = usePolicy();
const { accountScopedRoute } = useAccount();
const home = useSalesHome();

// A Central de Agendamento abre por cima da tela quando a conta tem a flag;
// sem ela, o bloco Agenda abre a visão de agenda do Kanban.
const showAgendaCard = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.AGENDA_CENTRAL)
);

const destination = id =>
  props.world.destinations.find(candidate => candidate.id === id) || null;
const withQuery = (dest, query) =>
  dest ? { ...dest.to, query: { ...dest.to.query, ...query } } : null;
const kanban = computed(() => destination('kanban'));
const go = to => {
  if (to) router.push(to);
};
// Mesmo "Voltar" da tela genérica das áreas: Vender não tinha caminho de
// volta ao início.
const goToHub = () => router.push(accountScopedRoute('focus_hub'));
const openTile = tile => {
  if (tile.id === 'agenda' && showAgendaCard.value) {
    emitter.emit('agenda:quick:open');
    return;
  }
  go(tile.to);
};
const openCard = card => go(withQuery(kanban.value, { item: String(card.id) }));

const tiles = computed(() =>
  [
    {
      id: 'tasks',
      icon: 'i-ph-check-square',
      title: t('HUB.STATS.TASKS_TODAY'),
      subtitle: `${t('HUB.LIVE.TASKS_TODAY', home.tasksToday.value, {
        count: home.tasksToday.value,
      })} · ${
        home.tasksOverdue.value
          ? t('HUB.STATS.TASKS_OVERDUE', home.tasksOverdue.value, {
              count: home.tasksOverdue.value,
            })
          : t('HUB.STATS.NONE_OVERDUE')
      }`,
      badge: home.tasksOverdue.value,
      to: destination('tasks')?.to,
    },
    {
      id: 'agenda',
      icon: 'i-ph-calendar',
      title: t('HUB.DESTINATIONS.AGENDA'),
      subtitle:
        home.agenda.value.count === null
          ? EMPTY
          : `${t('HUB.AGENDA_CARD.TODAY_COUNT', {
              count: home.agenda.value.count,
            })} · ${
              home.agenda.value.next
                ? t('HUB.STATS.AGENDA_NEXT', { when: home.agenda.value.next })
                : t('HUB.STATS.NO_EVENTS')
            }`,
      badge: 0,
      to: destination('agenda')?.to,
    },
    {
      id: 'funnel',
      icon: 'i-ph-funnel',
      title: t('HUB.SALES.FUNNEL'),
      subtitle: home.funnelSummary.value || t('HUB.HOME.FUNNEL_NONE'),
      badge: 0,
      to: kanban.value?.to,
    },
  ].filter(tile => tile.to)
);

const links = computed(() =>
  [
    {
      id: 'contacts',
      icon: 'i-ph-address-book',
      label: t('HUB.SALES.PEOPLE'),
      to: destination('contacts')?.to,
    },
    {
      id: 'reports_sales',
      icon: 'i-ph-chart-bar',
      label: t('HUB.HOME.SALES_REPORTS'),
      to: destination('reports_sales')?.to,
    },
    {
      id: 'kanban_funnels',
      icon: 'i-ph-gear',
      label: t('HUB.SALES.SETTINGS'),
      to: destination('kanban_funnels')?.to,
    },
  ].filter(link => link.to)
);
</script>

<template>
  <div
    class="w-full flex-1 px-5 py-7 sm:px-6 md:px-10 md:py-9"
    data-testid="sales-home"
  >
    <div class="mx-auto max-w-[1180px]">
      <button
        type="button"
        class="mb-4 inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-bold text-n-slate-11 transition-colors hover:bg-n-solid-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        data-testid="sales-back"
        @click="goToHub"
      >
        <span class="i-ri-arrow-left-line size-4" aria-hidden="true" />
        {{ t('HUB.AREA_BACK') }}
      </button>
      <header class="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 class="sal-title">{{ t('HUB.WORLDS.SALES') }}</h2>
          <p class="sal-sub">
            {{ home.funnel.value?.name || t('HUB.HOME.FUNNEL_NONE') }} ·
            {{ t('HUB.SALES.PERIOD') }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="hub-cta hub-cta--ghost min-h-11"
            data-testid="hub-action-new_deal"
            @click="go(withQuery(kanban, { new: '1' }))"
          >
            {{ t('HUB.HOME.NEW_DEAL') }}
          </button>
          <button
            type="button"
            class="hub-cta hub-cta--glow min-h-11"
            data-testid="hub-primary-sales"
            @click="go(kanban?.to)"
          >
            {{ t('HUB.OPEN_KANBAN') }}
          </button>
        </div>
      </header>

      <div class="sal-band">
        <div class="grid gap-3">
          <div class="hub-stat sal-main" data-testid="sales-open">
            <div class="hub-stat-l">{{ t('HUB.SALES.OPEN') }}</div>
            <div class="hub-stat-v sal-main-v">
              {{ home.stats.value.open.value }}
            </div>
            <div v-if="home.stats.value.open.error" class="hub-stat-h sal-err">
              {{ t('HUB.HOME.KPI_ERROR') }}
              <button type="button" class="sal-retry" @click="home.retryStats">
                {{ t('HUB.HOME.RETRY') }}
              </button>
            </div>
            <div v-else class="hub-stat-h">
              {{ home.stats.value.open.hint }}
            </div>
          </div>
          <div class="hub-stat" data-testid="sales-won">
            <div class="hub-stat-l">{{ t('HUB.SALES.WON_MONTH') }}</div>
            <div class="hub-stat-v">
              {{ home.stats.value.won.value }}
              <span
                v-if="home.stats.value.won.delta"
                class="hub-delta"
                :class="{ 'hub-delta--down': home.stats.value.won.down }"
              >
                {{ home.stats.value.won.delta }}
              </span>
            </div>
            <div v-if="home.stats.value.won.error" class="hub-stat-h sal-err">
              {{ t('HUB.HOME.KPI_ERROR') }}
              <button type="button" class="sal-retry" @click="home.retryStats">
                {{ t('HUB.HOME.RETRY') }}
              </button>
            </div>
            <div v-else class="hub-stat-h">{{ home.stats.value.won.hint }}</div>
          </div>
          <div class="hub-stat" data-testid="sales-rate">
            <div class="hub-stat-l">{{ t('HUB.HOME.KPI_WIN_RATE') }}</div>
            <div class="hub-stat-v">
              {{ home.stats.value.rate.value }}
              <span
                v-if="home.stats.value.rate.delta"
                class="hub-delta"
                :class="{ 'hub-delta--down': home.stats.value.rate.down }"
              >
                {{ home.stats.value.rate.delta }}
              </span>
            </div>
            <div v-if="home.stats.value.rate.error" class="hub-stat-h sal-err">
              {{ t('HUB.HOME.KPI_ERROR') }}
              <button type="button" class="sal-retry" @click="home.retryStats">
                {{ t('HUB.HOME.RETRY') }}
              </button>
            </div>
            <div v-else class="hub-stat-h">
              {{ home.stats.value.rate.hint }}
            </div>
          </div>
        </div>
        <div class="hub-stat sal-chart" data-testid="sales-chart">
          <div class="flex items-baseline justify-between gap-3">
            <b class="text-sm">{{ t('HUB.SALES.LAST_30') }}</b>
            <span class="sal-muted text-xs">{{
              t('HUB.SALES.CHART_HINT')
            }}</span>
          </div>
          <svg
            v-if="home.chart.value"
            class="sal-svg"
            viewBox="0 0 600 190"
            preserveAspectRatio="none"
            role="img"
            :aria-label="t('HUB.SALES.CHART_ARIA')"
          >
            <defs>
              <linearGradient id="sal-open" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color="#3ea6ff" stop-opacity="0.45" />
                <stop offset="1" stop-color="#3ea6ff" stop-opacity="0" />
              </linearGradient>
              <linearGradient id="sal-won" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color="#2dd4bf" stop-opacity="0.4" />
                <stop offset="1" stop-color="#2dd4bf" stop-opacity="0" />
              </linearGradient>
            </defs>
            <g class="sal-grid">
              <line x1="0" y1="45" x2="600" y2="45" />
              <line x1="0" y1="90" x2="600" y2="90" />
              <line x1="0" y1="135" x2="600" y2="135" />
            </g>
            <path :d="home.chart.value.openArea" fill="url(#sal-open)" />
            <path
              :d="home.chart.value.openLine"
              fill="none"
              stroke="#3ea6ff"
              stroke-width="2.5"
            />
            <path :d="home.chart.value.wonArea" fill="url(#sal-won)" />
            <path
              :d="home.chart.value.wonLine"
              fill="none"
              stroke="#2dd4bf"
              stroke-width="2.5"
            />
            <circle
              cx="600"
              :cy="home.chart.value.endY(home.chart.value.lastOpen)"
              r="5"
              fill="#3ea6ff"
            />
            <circle
              cx="600"
              :cy="home.chart.value.endY(home.chart.value.lastWon)"
              r="5"
              fill="#2dd4bf"
            />
          </svg>
          <p v-else-if="home.timelineLoaded.value" class="sal-empty">
            {{ t('HUB.SALES.CHART_EMPTY') }}
          </p>
          <div v-if="home.chart.value" class="sal-legend">
            <span>
              <i class="sal-dot sal-dot--open" />
              {{ t('HUB.SALES.OPEN') }} · {{ home.chart.value.openLabel }}
            </span>
            <span>
              <i class="sal-dot sal-dot--won" />
              {{ t('HUB.SALES.WON_CUMULATIVE') }} ·
              {{ home.chart.value.wonLabel }}
            </span>
            <span class="sal-muted ml-auto">{{ home.chart.value.range }}</span>
          </div>
        </div>
      </div>

      <div class="mt-3 grid gap-3 sm:grid-cols-3" data-testid="sales-today">
        <button
          v-for="tile in tiles"
          :key="tile.id"
          type="button"
          class="hub-tile"
          :data-testid="`hub-dest-${tile.id}`"
          @click="openTile(tile)"
        >
          <span class="hub-tile-ic">
            <span class="size-5" :class="[tile.icon]" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <span class="hub-tile-t">{{ tile.title }}</span>
            <span class="hub-tile-s">{{ tile.subtitle }}</span>
          </span>
          <span v-if="tile.badge" class="hub-tile-n sal-amber">{{
            tile.badge
          }}</span>
        </button>
      </div>

      <div
        v-if="home.columns.value.length"
        class="sal-board"
        data-testid="sales-board"
      >
        <div
          v-for="column in home.columns.value"
          :key="column.key"
          class="sal-col"
        >
          <h3 class="sal-col-h">
            <span>{{ column.name }}</span>
            <span>{{ column.count }}</span>
          </h3>
          <button
            v-for="card in column.cards"
            :key="card.id"
            type="button"
            class="sal-card"
            @click="openCard(card)"
          >
            <b>{{ card.title }}</b>
            <span>
              {{ [card.value, card.agent].filter(Boolean).join(' · ') }}
            </span>
          </button>
          <p v-if="!column.cards.length" class="sal-col-empty">
            {{ t('HUB.SALES.STAGE_EMPTY') }}
          </p>
        </div>
      </div>

      <nav
        class="mt-4 flex flex-wrap gap-x-5 gap-y-2"
        :aria-label="t('HUB.HOME.LINKS_LABEL')"
      >
        <button
          v-for="link in links"
          :key="link.id"
          type="button"
          class="sal-link min-h-9"
          :data-testid="`hub-link-${link.id}`"
          @click="go(link.to)"
        >
          <span class="size-4" :class="[link.icon]" aria-hidden="true" />
          {{ link.label }}
        </button>
      </nav>
    </div>
  </div>
</template>

<style scoped>
.sal-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.sal-sub {
  margin: 6px 0 0;
  color: var(--hv-fg2);
  font-size: 14px;
}
.sal-band {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 14px;
}
.sal-main {
  background: linear-gradient(
      135deg,
      rgba(62, 166, 255, 0.28),
      rgba(62, 166, 255, 0.06)
    ),
    var(--hv-el);
  border-color: rgba(62, 166, 255, 0.4);
}
.sal-main-v {
  font-size: 40px;
}
.sal-chart {
  display: flex;
  flex-direction: column;
  border-radius: 18px;
}
.sal-svg {
  width: 100%;
  height: 190px;
  display: block;
  margin-top: 8px;
}
.sal-grid line {
  stroke: var(--hv-line);
  stroke-width: 1;
}
.sal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: var(--hv-fg2);
  margin-top: 6px;
}
.sal-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: -1px;
}
.sal-dot--open {
  background: #3ea6ff;
}
.sal-dot--won {
  background: #2dd4bf;
}
.sal-muted {
  color: var(--hv-fg3);
}
.sal-empty {
  margin: 24px 0;
  font-size: 13px;
  color: var(--hv-fg3);
}
.sal-err {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--hv-amber-ink);
}
.sal-retry {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: var(--hv-accent);
  cursor: pointer;
  text-decoration: underline;
}
.sal-amber {
  background: var(--hv-amber-bg);
  color: var(--hv-amber-ink);
}
.sal-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
  margin-top: 14px;
}
.sal-col {
  background: var(--hv-surface);
  border: 1px solid var(--hv-line);
  border-radius: 14px;
  padding: 10px;
}
.sal-col-h {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  justify-content: space-between;
  color: var(--hv-fg2);
}
.sal-card {
  display: block;
  width: 100%;
  text-align: start;
  background: var(--hv-el);
  border: 1px solid var(--hv-line);
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
  font: inherit;
  font-size: 12px;
  color: var(--hv-fg);
  cursor: pointer;
}
.sal-card:hover {
  border-color: var(--hv-line2);
}
.sal-card:focus-visible {
  outline: 2px solid var(--hv-accent);
  outline-offset: 2px;
}
.sal-card b {
  display: block;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sal-card span {
  color: var(--hv-fg3);
}
.sal-col-empty {
  margin: 8px 2px;
  font-size: 11px;
  color: var(--hv-fg3);
}
.sal-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--hv-fg3);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.sal-link:hover {
  color: var(--hv-fg);
}
@media (max-width: 860px) {
  .sal-band {
    grid-template-columns: 1fr;
  }
}
</style>
